import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { auth } from '../utils/firebase';
import { RecaptchaVerifier, signInWithPhoneNumber, sendSignInLinkToEmail } from 'firebase/auth';
import { toast } from 'react-toastify';

export default function LoginForm({ onSuccess, recaptchaContainerId = 'recaptcha-container' }) {
  const { t } = useTranslation();
  const [tab, setTab] = useState('phone'); // 'phone' | 'email'
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [confirmResult, setConfirmResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const setupRecaptcha = () => {
    if (!window.recaptchaVerifier) {
      window.recaptchaVerifier = new RecaptchaVerifier(auth, recaptchaContainerId, { size: 'normal' });
    }
  };

  const sendPhoneOTP = async () => {
    if (!phone || phone.length < 10) return toast.error(t('login.toastInvalidPhone'));
    if (!name.trim()) return toast.error(t('login.toastEnterName'));
    setLoading(true);
    try {
      setupRecaptcha();
      const result = await signInWithPhoneNumber(auth, `+91${phone}`, window.recaptchaVerifier);
      setConfirmResult(result);
      setOtpSent(true);
      localStorage.setItem('pendingName', name);
      toast.success(t('login.toastOtpSent', { phone }));
    } catch (err) {
      toast.error(err.message);
      window.recaptchaVerifier = null;
    }
    setLoading(false);
  };

  const verifyPhoneOTP = async () => {
    if (!otp || otp.length !== 6) return toast.error(t('login.toastEnterOtp'));
    setLoading(true);
    try {
      await confirmResult.confirm(otp);
      // AuthContext onAuthStateChanged will handle token exchange
      toast.success(t('login.toastLoginSuccess'));
      onSuccess?.();
    } catch (err) {
      toast.error(t('login.toastInvalidOtp'));
    }
    setLoading(false);
  };

  const sendEmailLink = async () => {
    if (!email || !email.includes('@')) return toast.error(t('login.toastInvalidEmail'));
    if (!name.trim()) return toast.error(t('login.toastEnterName'));
    setLoading(true);
    try {
      await sendSignInLinkToEmail(auth, email, {
        url: window.location.href,
        handleCodeInApp: true,
      });
      localStorage.setItem('emailForSignIn', email);
      localStorage.setItem('pendingName', name);
      setOtpSent(true);
      toast.success(t('login.toastEmailLinkSent', { email }));
    } catch (err) {
      toast.error(err.message);
    }
    setLoading(false);
  };

  return (
    <>
      <div className="auth-logo">
        <div style={{ fontSize: '3rem' }}>🐂</div>
        <h2>Goshala | गोशाला</h2>
        <p>{t('login.subtitle')}</p>
      </div>

      {!otpSent && (
        <>
          <div className="form-group">
            <label className="form-label">{t('login.yourName')}</label>
            <input className="form-input" placeholder={t('login.namePlaceholder')} value={name} onChange={e => setName(e.target.value)} />
          </div>

          <div className="auth-tabs">
            <button className={`auth-tab${tab === 'phone' ? ' active' : ''}`} onClick={() => setTab('phone')}>{t('login.tabPhone')}</button>
            <button className={`auth-tab${tab === 'email' ? ' active' : ''}`} onClick={() => setTab('email')}>{t('login.tabEmail')}</button>
          </div>

          {tab === 'phone' && (
            <>
              <div className="form-group">
                <label className="form-label">{t('login.mobileNumber')}</label>
                <div className="phone-input-group">
                  <span className="phone-prefix">🇮🇳 +91</span>
                  <input className="form-input" placeholder={t('login.phonePlaceholder')} value={phone} onChange={e => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))} type="tel" maxLength={10} />
                </div>
              </div>
              <div id={recaptchaContainerId} className="recaptcha-container" />
              <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }} onClick={sendPhoneOTP} disabled={loading}>
                {loading ? t('login.sending') : t('login.sendOtp')}
              </button>
            </>
          )}

          {tab === 'email' && (
            <>
              <div className="form-group">
                <label className="form-label">{t('login.emailAddress')}</label>
                <input className="form-input" type="email" placeholder={t('login.emailPlaceholder')} value={email} onChange={e => setEmail(e.target.value)} />
              </div>
              <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }} onClick={sendEmailLink} disabled={loading}>
                {loading ? t('login.sending') : t('login.sendSignInLink')}
              </button>
            </>
          )}
        </>
      )}

      {otpSent && tab === 'phone' && (
        <div>
          <div style={{ textAlign: 'center', marginBottom: 20 }}>
            <div style={{ fontSize: '2rem' }}>📱</div>
            <h3 style={{ marginBottom: 8 }}>{t('login.enterOtp')}</h3>
            <p style={{ color: '#888', fontSize: '0.9rem' }}>{t('login.otpSentTo', { phone })}</p>
          </div>
          <div className="form-group">
            <input className="form-input" placeholder={t('login.otpPlaceholder')} value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))} type="tel" maxLength={6} style={{ textAlign: 'center', fontSize: '1.4rem', letterSpacing: 8 }} />
          </div>
          <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }} onClick={verifyPhoneOTP} disabled={loading}>
            {loading ? t('login.verifying') : t('login.verifyAndLogin')}
          </button>
          <button style={{ width: '100%', marginTop: 10, background: 'none', border: 'none', color: 'var(--saffron)', cursor: 'pointer', fontSize: '0.9rem' }} onClick={() => { setOtpSent(false); window.recaptchaVerifier = null; }}>
            {t('login.changeNumber')}
          </button>
        </div>
      )}

      {otpSent && tab === 'email' && (
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <div style={{ fontSize: '3rem', marginBottom: 12 }}>📧</div>
          <h3 style={{ marginBottom: 8 }}>{t('login.checkEmail')}</h3>
          <p style={{ color: '#777', lineHeight: 1.7 }}>
            {t('login.linkSentTo', { email })}<br />
            {t('login.clickLinkToLogin')}
          </p>
          <button style={{ marginTop: 16, background: 'none', border: 'none', color: 'var(--saffron)', cursor: 'pointer', fontSize: '0.9rem' }} onClick={() => setOtpSent(false)}>
            {t('login.tryDifferentEmail')}
          </button>
        </div>
      )}

      <p style={{ textAlign: 'center', marginTop: 20, fontSize: '0.85rem', color: '#888' }}>
        {t('login.agreeTermsPrefix')} <Link to="#">{t('login.terms')}</Link> {t('login.and')} <Link to="#">{t('login.privacyPolicy')}</Link>
      </p>
    </>
  );
}
